import './source-evidence-section.css';

import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { flex, px } from '@kerfjs/ui/css-values';
import { List } from '@kerfjs/ui/list';
import { Row } from '@kerfjs/ui/row';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';

import type { SourceCandidate, SourceEvidenceResponse } from '../../../../shared/contract/library.js';
import { sourceRowPaid, sourceRowProvenance, sourceRowStatusLabel } from '../../../lib/source-row-provenance.js';

type EvidenceEntry = SourceEvidenceResponse['evidence'][number];

const STATUS_TONE: Record<SourceCandidate['status'], 'neutral' | 'success' | 'danger'> = {
  suggested: 'neutral',
  confirmed: 'success',
  rejected: 'danger',
};

/**
 * A demand's Inventory and Purchase History (FR-10.6a): per line, its confirmed last-paid price
 * and the stock and purchase rows suggested or matched for it, each with its provenance and a
 * status badge, plus Match, Reject, and Find a Source Row… while the demand is editable. Each line
 * is a framed Kerf ContentItem whose rows share the card's control insets (HS2-YENW3T).
 */
export function SourceEvidenceSection({
  entries,
  lineDescriptions,
  readOnly,
}: {
  /** The lines to show, already narrowed to those with something to review. */
  entries: readonly EvidenceEntry[];
  /** Each line's description by line id. */
  lineDescriptions: ReadonlyMap<string, string>;
  readOnly: boolean;
}): SafeHtml {
  if (entries.length === 0) return <></>;
  return (
    <section class="source-evidence-section" aria-label="Inventory and Purchase History">
      <List gap="m">
        <Text variant="h3">Inventory and Purchase History</Text>
        {entries.map((entry) => (
          <ContentItem
            frame="framed"
            appearance="surface"
            className="source-evidence-line"
            rootAttributes={{ 'data-key': entry.lineItemId }}
          >
            <List gap="xs" controlInsets="tb">
              <Text variant="h4" controlMargins="rl">
                {lineDescriptions.get(entry.lineItemId) ?? 'Demand line'}
              </Text>
              {entry.lastPaid !== null ? (
                <Text controlMargins="rl">Last paid {sourceRowPaid(entry.lastPaid) ?? '—'}</Text>
              ) : null}
              {entry.candidates.length === 0 ? (
                <Text size="compact" tone="quiet" controlMargins="rl">
                  No suggested rows.
                </Text>
              ) : null}
              {entry.candidates.map((candidate) => (
                <Row
                  className="source-evidence-row"
                  gap="xs"
                  vAlign="middle"
                  wrap
                  controlInsets="rl"
                  rootAttributes={{ 'data-key': candidate.recordId }}
                >
                  {/* A basis wide enough for the provenance keeps the status and actions on the row's
                      line when there is room; a narrow card wraps them below. */}
                  <List flex={flex(1, 1, px(320))} gap="none">
                    <Text wrap="anywhere" flush controlMargins="rl">
                      {candidate.description}
                    </Text>
                    <Text size="compact" tone="quiet" wrap="anywhere" flush controlMargins="rl">
                      {sourceRowProvenance(candidate)}
                    </Text>
                  </List>
                  <Badge tone={STATUS_TONE[candidate.status]} shape="rounded" size="compact">
                    {sourceRowStatusLabel(candidate.status)}
                  </Badge>
                  {!readOnly && candidate.status !== 'confirmed' ? (
                    <Button
                      tone="neutral"
                      emphasis="primary"
                      size="compact"
                      rootAttributes={{
                        'data-source-match': `${candidate.kind}:${candidate.recordId}:${entry.lineItemId}:confirmed`,
                      }}
                    >
                      Match
                    </Button>
                  ) : null}
                  {!readOnly && candidate.status !== 'rejected' ? (
                    <Button
                      tone="neutral"
                      size="compact"
                      emphasis="plain"
                      rootAttributes={{
                        'data-source-match': `${candidate.kind}:${candidate.recordId}:${entry.lineItemId}:rejected`,
                      }}
                    >
                      Reject
                    </Button>
                  ) : null}
                </Row>
              ))}
              {readOnly ? null : (
                <Row controlInsets="rl">
                  <Button
                    tone="neutral"
                    size="compact"
                    emphasis="secondary"
                    action="source-row-find"
                    rootAttributes={{ 'data-line-item-id': entry.lineItemId }}
                  >
                    Find a Source Row…
                  </Button>
                </Row>
              )}
            </List>
          </ContentItem>
        ))}
      </List>
    </section>
  );
}
